import { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  Divider, 
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  IconButton
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink } from 'react-router-dom';
import { neighborBusinessService } from '../services/api';

// Category options matching the Strapi schema
const categoryOptions = [
  { value: 'home_lawn_services', label: 'Home & Lawn Services' },
  { value: 'construction_renovation', label: 'Construction & Renovation' },
  { value: 'professional_legal', label: 'Professional & Legal' },
  { value: 'financial_insurance', label: 'Financial & Insurance' },
  { value: 'retail', label: 'Retail' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'health_beauty_fitness', label: 'Health, Beauty & Fitness' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'childcare_education', label: 'Childcare & Education' },
  { value: 'pet_services', label: 'Pet Services' },
  { value: 'technology', label: 'Technology' },
  { value: 'creative_events', label: 'Creative & Events' },
  { value: 'other', label: 'Other' }
];

const BusinessSubmissionForm = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    category: '',
    description: '',
    phone: '',
    email: '',
    website: '',
    address: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setErrors({ ...errors, logo: 'Please upload a valid image file (JPEG, PNG, GIF, or WebP)' });
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErrors({ ...errors, logo: 'Image must be less than 2MB' });
        return;
      }
      
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setErrors({ ...errors, logo: null });
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
      setLogoPreview(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear validation error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Business description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Please provide a more detailed description (at least 20 characters)';
    }
    
    if (!formData.phone.trim() && !formData.email.trim()) {
      newErrors.phone = 'At least one contact method (phone or email) is required';
      newErrors.email = 'At least one contact method (phone or email) is required';
    }
    
    if (formData.phone.trim() && !/^[\d\s\-()]{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.website.trim() && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/.test(formData.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const result = await neighborBusinessService.submitBusiness(formData, logoFile);
      
      if (result.success) {
        setSubmitSuccess(true);
        setFormData({
          businessName: '',
          ownerName: '',
          category: '',
          description: '',
          phone: '',
          email: '',
          website: '',
          address: ''
        });
        handleRemoveLogo();
      } else {
        setSubmitError(result.error || 'Failed to submit business. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting business:', error);
      setSubmitError('There was a problem submitting your business. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      businessName: '',
      ownerName: '',
      category: '',
      description: '',
      phone: '',
      email: '',
      website: '',
      address: ''
    });
    handleRemoveLogo();
    setErrors({});
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  return (
    <>
      <Helmet>
        <title>Submit Your Business | Brynleigh Estates HOA</title>
        <meta name="description" content="Submit your local business to be featured in the Brynleigh Estates Neighbor Business Directory. Support local entrepreneurs in our community." />
        <link rel="canonical" href="https://brynleighestates.com/submit-business" />
      </Helmet>
      
      <Hero 
        title="Submit Your Business" 
        subtitle="Get your local business listed in our community directory"
        imageUrl="/images/hills-bg.jpeg"
      />
      
      <Container maxWidth="md" sx={{ py: 6 }}>
        {submitSuccess ? (
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" component="h2" gutterBottom>
              Business Submitted Successfully
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Thank you for submitting your business to the Brynleigh Estates directory. 
              Your listing will be reviewed by the HOA board and will appear in the directory once approved.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This process typically takes 3-5 business days.
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                component={RouterLink}
                to="/neighbor-businesses"
              >
                View Business Directory
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleReset}
              >
                Submit Another Business
              </Button>
            </Box>
          </Paper>
        ) : (
          <>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Business Information
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fill out the form below to submit your business for inclusion in our neighbor business directory. 
                All submissions are reviewed before being published.
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              {submitError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {submitError}
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Business Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Business Name"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      error={!!errors.businessName}
                      helperText={errors.businessName}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Owner Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Owner Name"
                      name="ownerName"
                      value={formData.ownerName}
                      onChange={handleChange}
                      error={!!errors.ownerName}
                      helperText={errors.ownerName || 'Name of the Brynleigh Estates resident'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Category */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required error={!!errors.category}>
                      <InputLabel id="category-label">Business Category</InputLabel>
                      <Select
                        labelId="category-label"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        label="Business Category"
                        startAdornment={
                          <InputAdornment position="start">
                            <CategoryIcon color="action" />
                          </InputAdornment>
                        }
                      >
                        {categoryOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  
                  {/* Phone */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Email */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Website */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      error={!!errors.website}
                      helperText={errors.website || 'Optional'}
                      placeholder="https://www.example.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Address */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Business Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      error={!!errors.address}
                      helperText={errors.address || 'Optional - Leave blank if home-based or service area business'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  {/* Logo Upload */}
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px dashed', borderColor: errors.logo ? 'error.main' : 'divider', borderRadius: 1, p: 3 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon color="action" />
                        Business Logo (Optional)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Upload your business logo (JPEG, PNG, GIF, or WebP - max 2MB)
                      </Typography>
                      
                      {logoPreview ? (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            component="img"
                            src={logoPreview}
                            alt="Logo preview"
                            sx={{
                              maxHeight: 100,
                              maxWidth: 200,
                              objectFit: 'contain',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1,
                              bgcolor: 'grey.50'
                            }}
                          />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {logoFile?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {logoFile ? `${(logoFile.size / 1024).toFixed(1)} KB` : ''}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={handleRemoveLogo}
                                title="Remove logo"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<UploadFileIcon />}
                          sx={{ mt: 1 }}
                        >
                          Select Logo
                          <input
                            type="file"
                            hidden
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={handleLogoChange}
                          />
                        </Button>
                      )}
                      
                      {errors.logo && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                          {errors.logo}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  {/* Description */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={4}
                      label="Business Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      error={!!errors.description}
                      helperText={errors.description || 'Describe your business, services offered, and what makes you stand out'}
                    />
                  </Grid>
                  
                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                      <Button 
                        variant="outlined" 
                        onClick={handleReset}
                        disabled={isSubmitting}
                      >
                        Clear Form
                      </Button>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        size="large"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Business'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Paper>
            
            <Paper sx={{ p: 3, bgcolor: 'info.50' }}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Submission Guidelines
              </Typography>
              <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2 }}>
                <li>Business must be owned or operated by a Brynleigh Estates resident</li>
                <li>All submissions are subject to HOA board review and approval</li>
                <li>Listings are free of charge for community members</li>
                <li>The HOA reserves the right to decline or remove listings</li>
                <li>Please allow 3-5 business days for review</li>
              </Typography>
            </Paper>
          </>
        )}
      </Container>
    </>
  );
};

export default BusinessSubmissionForm;
